import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowLeft, Lock, Server, Eye, FileCheck, Users, AlertTriangle, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { LOGO_URL } from '@/lib/brand';

const Security = () => {
    const lastUpdated = 'December 19, 2025';

    const securityMeasures = [
        {
            icon: Lock,
            title: 'Data Encryption',
            description: 'All data encrypted in transit (TLS 1.3) and at rest (AES-256)'
        },
        {
            icon: Server,
            title: 'Secure Infrastructure',
            description: 'SOC 2 Type II compliant cloud infrastructure with redundancy'
        },
        {
            icon: Eye,
            title: 'Access Controls',
            description: 'Role-based access control with multi-factor authentication'
        },
        {
            icon: FileCheck,
            title: 'Audit Logging',
            description: 'Immutable audit trails for all system and AI agent actions'
        },
        {
            icon: Users,
            title: 'Employee Security',
            description: 'Background checks and ongoing security training for all staff'
        },
        {
            icon: AlertTriangle,
            title: 'Incident Response',
            description: '24/7 security monitoring with rapid incident response'
        }
    ];

    return (
        <div className="min-h-screen bg-[#0a0a0f] text-white">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0f]/80 backdrop-blur-lg border-b border-white/5">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3">
                        <img src={LOGO_URL} alt="Shiro Security" className="w-10 h-10 object-contain" />
                        <span className="text-xl font-bold gradient-text">SHIRO</span>
                        <span className="text-xl font-light text-white/80">Security</span>
                    </Link>
                    <Link to="/">
                        <Button variant="ghost" className="text-white/70 hover:text-white hover:bg-white/5">
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Back to Home
                        </Button>
                    </Link>
                </div>
            </nav>

            {/* Hero */}
            <section className="pt-32 pb-16 px-6 hero-gradient">
                <div className="max-w-4xl mx-auto text-center">
                    <h1 className="text-5xl font-bold mb-6">Security at Shiro</h1>
                    <p className="text-xl text-white/60">
                        We practice what we preach. Learn how we protect your data and our platform.
                    </p>
                    <p className="text-white/40 mt-4">Last Updated: {lastUpdated}</p>
                </div>
            </section>

            {/* Security Measures Grid */}
            <section className="py-16 px-6">
                <div className="max-w-6xl mx-auto">
                    <h2 className="text-3xl font-bold text-center mb-12">How We Protect Your Data</h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {securityMeasures.map((measure, index) => (
                            <Card key={index} className="glass-card border-0">
                                <CardContent className="p-6">
                                    <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center mb-4">
                                        <measure.icon className="w-6 h-6 text-cyan-400" />
                                    </div>
                                    <h3 className="text-lg font-bold mb-2">{measure.title}</h3>
                                    <p className="text-white/60 text-sm">{measure.description}</p>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* Detailed Sections */}
            <div className="pb-20 px-6">
                <div className="max-w-4xl mx-auto space-y-8">
                    <section className="glass-card rounded-xl p-8">
                        <h2 className="text-2xl font-bold mb-4 text-cyan-400">1. Infrastructure Security</h2>
                        <p className="text-white/80 leading-relaxed mb-4">
                            Shiro Security's platform is hosted on enterprise-grade cloud infrastructure with the following security controls:
                        </p>
                        <ul className="list-disc list-inside text-white/80 space-y-2">
                            <li>SOC 2 Type II certified data centers</li>
                            <li>Geographic redundancy across multiple availability zones</li>
                            <li>DDoS protection and Web Application Firewall (WAF)</li>
                            <li>Network segmentation and micro-segmentation</li>
                            <li>Regular vulnerability scanning and penetration testing</li>
                            <li>Automated security patching and updates</li>
                        </ul>
                    </section>

                    <section className="glass-card rounded-xl p-8">
                        <h2 className="text-2xl font-bold mb-4 text-cyan-400">2. Data Protection</h2>
                        
                        <h3 className="text-xl font-semibold mb-3 text-white">2.1 Encryption</h3>
                        <ul className="list-disc list-inside text-white/80 space-y-2 mb-4">
                            <li><strong>In Transit:</strong> TLS 1.3 encryption for all data transmission</li>
                            <li><strong>At Rest:</strong> AES-256 encryption for stored data</li>
                            <li><strong>Key Management:</strong> Hardware Security Modules (HSM) for cryptographic keys</li>
                        </ul>

                        <h3 className="text-xl font-semibold mb-3 text-white">2.2 Data Segregation</h3>
                        <p className="text-white/80 leading-relaxed mb-4">
                            Each client's data is logically segregated using unique encryption keys and access controls. No client can access another client's data.
                        </p>

                        <h3 className="text-xl font-semibold mb-3 text-white">2.3 Data Retention</h3>
                        <p className="text-white/80 leading-relaxed">
                            Data retention periods are configurable based on your subscription plan and compliance requirements. Data is securely deleted upon request or account termination.
                        </p>
                    </section>

                    <section className="glass-card rounded-xl p-8">
                        <h2 className="text-2xl font-bold mb-4 text-cyan-400">3. AI Security & Transparency</h2>
                        <p className="text-white/80 leading-relaxed mb-4">
                            Our AI Agents (Watchdog, Investigator, and Jurist) are designed with security and transparency as core principles:
                        </p>
                        <ul className="list-disc list-inside text-white/80 space-y-2">
                            <li><strong>Immutable Audit Trail:</strong> Every AI decision is logged with full reasoning and cannot be altered</li>
                            <li><strong>Explainability:</strong> All AI actions include detailed justification for review</li>
                            <li><strong>Human Oversight:</strong> Critical actions require human authorization before execution</li>
                            <li><strong>Bias Testing:</strong> Regular evaluation to prevent discriminatory patterns</li>
                            <li><strong>Model Security:</strong> AI models are protected against adversarial attacks and prompt injection</li>
                            <li><strong>Data Isolation:</strong> AI training uses anonymized, aggregated data; your specific data is never used to train models</li>
                        </ul>
                    </section>

                    <section className="glass-card rounded-xl p-8">
                        <h2 className="text-2xl font-bold mb-4 text-cyan-400">4. Access Controls</h2>
                        <ul className="list-disc list-inside text-white/80 space-y-2">
                            <li><strong>Multi-Factor Authentication (MFA):</strong> Required for all accounts</li>
                            <li><strong>Single Sign-On (SSO):</strong> Available for Enterprise plans (SAML 2.0, OAuth 2.0)</li>
                            <li><strong>Role-Based Access Control (RBAC):</strong> Granular permissions by role</li>
                            <li><strong>Session Management:</strong> Automatic timeout and secure session handling</li>
                            <li><strong>API Security:</strong> Token-based authentication with rate limiting</li>
                            <li><strong>Privileged Access Management:</strong> Just-in-time access for administrative functions</li>
                        </ul>
                    </section>

                    <section className="glass-card rounded-xl p-8">
                        <h2 className="text-2xl font-bold mb-4 text-cyan-400">5. Compliance & Certifications</h2>
                        <p className="text-white/80 leading-relaxed mb-4">
                            Shiro Security maintains compliance with industry standards and regulations:
                        </p>
                        <div className="grid md:grid-cols-2 gap-4">
                            {[
                                { name: 'SOC 2 Type II', status: 'Certified' },
                                { name: 'ISO 27001', status: 'In Progress' },
                                { name: 'GDPR', status: 'Compliant' },
                                { name: 'CCPA', status: 'Compliant' },
                                { name: 'HIPAA', status: 'Available (Compliance Plan)' },
                                { name: 'PCI DSS', status: 'Level 1 Service Provider' }
                            ].map((cert, index) => (
                                <div key={index} className="flex items-center gap-3 bg-white/5 rounded-lg p-3">
                                    <CheckCircle className="w-5 h-5 text-green-400" />
                                    <div>
                                        <div className="font-medium">{cert.name}</div>
                                        <div className="text-xs text-white/50">{cert.status}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>

                    <section className="glass-card rounded-xl p-8">
                        <h2 className="text-2xl font-bold mb-4 text-cyan-400">6. Employee Security</h2>
                        <ul className="list-disc list-inside text-white/80 space-y-2">
                            <li>Background checks for all employees</li>
                            <li>Mandatory security awareness training</li>
                            <li>Principle of least privilege access</li>
                            <li>Secure development lifecycle (SDL) practices</li>
                            <li>Regular security training and phishing simulations</li>
                            <li>Confidentiality agreements and security policies</li>
                        </ul>
                    </section>

                    <section className="glass-card rounded-xl p-8">
                        <h2 className="text-2xl font-bold mb-4 text-cyan-400">7. Incident Response</h2>
                        <p className="text-white/80 leading-relaxed mb-4">
                            In the event of a security incident affecting our platform or your data:
                        </p>
                        <ul className="list-disc list-inside text-white/80 space-y-2">
                            <li><strong>Detection:</strong> 24/7 security monitoring with automated alerting</li>
                            <li><strong>Response:</strong> Dedicated incident response team on-call</li>
                            <li><strong>Notification:</strong> Affected clients notified within 72 hours (or sooner as required by law)</li>
                            <li><strong>Investigation:</strong> Thorough root cause analysis</li>
                            <li><strong>Remediation:</strong> Swift action to contain and resolve incidents</li>
                            <li><strong>Post-Incident:</strong> Detailed report and lessons learned</li>
                        </ul>
                    </section>

                    <section className="glass-card rounded-xl p-8 border border-cyan-500/30">
                        <h2 className="text-2xl font-bold mb-4 text-cyan-400">8. Responsible Disclosure Policy</h2>
                        <p className="text-white/80 leading-relaxed mb-4">
                            We value the security research community and welcome responsible disclosure of security vulnerabilities.
                        </p>
                        
                        <h3 className="text-xl font-semibold mb-3 text-white">How to Report</h3>
                        <p className="text-white/80 leading-relaxed mb-4">
                            If you discover a security vulnerability, please report it to: <span className="text-cyan-400">security@shirosec.com</span>
                        </p>

                        <h3 className="text-xl font-semibold mb-3 text-white">Our Commitment</h3>
                        <ul className="list-disc list-inside text-white/80 space-y-2 mb-4">
                            <li>Acknowledge receipt within 24 hours</li>
                            <li>Provide regular updates on remediation progress</li>
                            <li>Not pursue legal action against good-faith researchers</li>
                            <li>Credit researchers (with permission) in our security acknowledgments</li>
                        </ul>

                        <h3 className="text-xl font-semibold mb-3 text-white">Guidelines</h3>
                        <ul className="list-disc list-inside text-white/80 space-y-2">
                            <li>Do not access or modify other users' data</li>
                            <li>Do not perform denial of service attacks</li>
                            <li>Do not publicly disclose until we've had reasonable time to fix</li>
                            <li>Provide sufficient detail to reproduce the vulnerability</li>
                        </ul>
                    </section>

                    <section className="glass-card rounded-xl p-8">
                        <h2 className="text-2xl font-bold mb-4 text-cyan-400">9. Contact Us</h2>
                        <p className="text-white/80 leading-relaxed mb-4">
                            For security-related inquiries or to request our security documentation:
                        </p>
                        <div className="text-white/80">
                            <p><strong>Security Team</strong></p>
                            <p>Email: security@shirosec.com</p>
                            <p>PGP Key: Available upon request</p>
                        </div>
                        <p className="text-white/60 text-sm mt-4">
                            For compliance documentation, SOC 2 reports, or security questionnaires, please contact your account manager or email compliance@shirosec.com.
                        </p>
                    </section>
                </div>
            </div>

            {/* Footer */}
            <footer className="py-8 px-6 border-t border-white/5">
                <div className="max-w-7xl mx-auto text-center text-white/40 text-sm">
                    &copy; {new Date().getFullYear()} Shiro Security, LLC. All rights reserved.
                </div>
            </footer>
        </div>
    );
};

export default Security;
